import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";

const ZeroClickAttacks: React.FC = () => {
  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Zero-Click Attacks: The Ultimate Cybersecurity Challenge of 2026",
    description:
      "Complete guide to zero-click attacks, AI agents, and advanced cybersecurity challenges. Learn about how zero-click exploits work, their impact, and protection strategies for 2026.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/zero-click-attacks",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="Zero-Click Attacks: The Ultimate Cybersecurity Challenge 2026"
        description="Complete guide to zero-click attacks, AI agents, and advanced cybersecurity challenges."
        keywords="zero-click attacks, zero-click exploits, cybersecurity threats 2026, AI-powered attacks, zero-click protection, mobile security, iOS security, Android security, advanced persistent threats, cybersecurity challenges, zero-click vulnerabilities"
        canonical="https://cyberannals.com/blog/zero-click-attacks"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            Zero-Click Attacks
          </h1>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            The Ultimate Cybersecurity Challenge in 2026
          </h2>
          <h3>
            Understanding AI Agents and Advanced Exploitation Techniques
          </h3>
        </header>

        <div className="space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
          <p className="text-lg">
            Zero-click attacks represent the pinnacle of cybersecurity threats, requiring no user interaction to compromise devices. These sophisticated exploits target vulnerabilities in communication protocols, messaging systems, and operating systems, making them particularly dangerous as they can silently infect devices without any indication to users. As we advance through 2026, these attacks have become increasingly sophisticated, often leveraging artificial intelligence and machine learning to identify and exploit vulnerabilities automatically. Learn about <Link to="/decades" className={styles.link}>historical cyber attacks</Link> that paved the way for these advanced threats.
          </p>

          <div className="aspect-w-16 aspect-h-9 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden max-w-2xl mx-auto my-6">
            <iframe
            className="w-full h-full"
            src="https://www.youtube.com/embed/CMHL1bPtQdI?si=Ri-FSuc-TO9a2VUG"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
             referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
          </div>

          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 rounded-r-lg mt-8">
            <h3 className="font-bold text-lg text-red-800 dark:text-red-200 mb-2">
              Critical Threat Level
            </h3>
            <p className="text-red-700 dark:text-red-300">
              Zero-click attacks are considered among the most dangerous cyber threats because they require no user interaction, making traditional security awareness training ineffective. These attacks often target high-value individuals and organizations, and can remain undetected for extended periods.
            </p>
          </div>

          {/* Affiliate Section - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <h3 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">
              Advanced Threat Protection Solutions
            </h3>
            <p className="mb-4">
              Experience enterprise-grade zero-click attack protection with industry-leading solutions:
            </p>
            <div className="flex flex-col md:flex-row items-start gap-6 mb-4">
              <div className="flex-1">
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/assets/affiliates/zero-click-protection/banner.png"
                    alt="Zero-Click Protection Banner"
                    className="max-w-full h-auto rounded"
                  />
                </a>
              </div>
              <div className="flex-1">
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Real-time exploit detection and prevention</li>
                  <li>AI-powered threat analysis and response</li>
                  <li>Behavioral analysis and anomaly detection</li>
                  <li>24/7 expert monitoring and incident response</li>
                </ul>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-bold mb-2 text-red-600 dark:text-red-400">
                Understanding Zero-Click Vulnerabilities
              </h4>
              <div className="aspect-w-16 aspect-h-9 w-full max-w-full overflow-hidden rounded-lg">
                <iframe
                className="w-full h-full max-w-full max-h-full"
                src="https://www.youtube.com/embed/[ZERO_CLICK_UNDERSTANDING_VIDEO_ID]"
                title="Understanding Zero-Click Vulnerabilities"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
                </iframe>
              </div>
            </div>
          </div>
          */}

          {/* Primary Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-red-600 to-orange-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Defend Against Zero-Click Attacks</h3>
              <p className="mb-4">Get expert analysis of zero-click protection strategies</p>
              <Link
                to="/toolkit"
                className="inline-block px-6 py-3 bg-white text-red-600 font-bold rounded-lg hover:bg-red-50 transition-colors"
              >
                Security Toolkit →
              </Link>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Understanding Zero-Click Attack Mechanisms
          </h2>

          <p>
            Zero-click attacks exploit vulnerabilities in system components that process incoming communications without user interaction. These attacks typically target:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Messaging Applications:</strong> Vulnerabilities in WhatsApp, iMessage, Signal, and other messaging platforms
            </li>
            <li>
              <strong>Network Protocols:</strong> Flaws in TCP/IP stack implementations, Wi-Fi protocols, or cellular communication
            </li>
            <li>
              <strong>Operating System Components:</strong> Flaws in system services that process network data automatically
            </li>
            <li>
              <strong>Communication Interfaces:</strong> Bluetooth, NFC, and other wireless communication protocols - Learn about <Link to="/blog/common-cyber-attacks" className={styles.link}>common attack vectors</Link> that exploit similar interfaces
            </li>
          </ul>

          <p className="mt-4">
            These attacks often use sophisticated techniques such as memory corruption exploits, buffer overflows, and race conditions to gain execution privileges on target devices. The <Link to="/event/stuxnet" className={styles.link}>Stuxnet attack</Link> demonstrated how sophisticated zero-click exploits could be used to target critical infrastructure without user interaction.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Notable Zero-Click Attack Examples
          </h3>

          <p>
            Several high-profile zero-click attacks have demonstrated the potential impact of these threats:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Pegasus Spyware:</strong> Exploited iOS and Android devices through iMessage and WhatsApp vulnerabilities
            </li>
            <li>
              <strong>iMessage Zero-Click Exploits:</strong> Targeted iPhones through specially crafted images and messages
            </li>
            <li>
              <strong>Android Stagefright:</strong> Exploited media processing vulnerabilities in Android devices
            </li>
            <li>
              <strong>BlueBorne:</strong> Wireless attack vector targeting Bluetooth implementations - Learn about <Link to="/blog/what-is-cyber-security" className={styles.link}>how these attacks exploit fundamental security flaws</Link>
            </li>
          </ul>

          <p className="mt-4">
            The <Link to="/event/sony-pictures-hack-2014" className={styles.link}>Sony Pictures attack</Link> showed how sophisticated attackers could maintain persistent access to systems using zero-click techniques that didn't require user interaction once initial access was obtained.
          </p>

          {/* Mid-content Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-teal-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Zero-Click Security Assessment</h3>
              <p className="mb-4">Get a comprehensive evaluation of your zero-click attack preparedness</p>
              <Link
                to="/toolkit"
                className="inline-block px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors"
              >
                Security Toolkit →
              </Link>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            AI Agents and Zero-Click Attack Evolution
          </h2>

          <p>
            The integration of artificial intelligence into zero-click attacks has created new challenges for cybersecurity professionals. AI agents can now:
          </p>

          <ol className="list-decimal pl-6 space-y-2 mt-4">
            <li>
              <strong>Automatically Discover Vulnerabilities:</strong> Use machine learning to identify potential zero-click attack vectors
            </li>
            <li>
              <strong>Adapt Exploits in Real-Time:</strong> Modify attack patterns based on defensive responses
            </li>
            <li>
              <strong>Target Specific Individuals:</strong> Use social engineering data to customize attacks
            </li>
            <li>
              <strong>Evade Detection Systems:</strong> Learn and bypass traditional security measures - Learn about <Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI-driven cyber attacks</Link> and their implications
            </li>
          </ol>

          <p className="mt-4">
            The <Link to="/event/solarwinds-supply-chain-attack" className={styles.link}>SolarWinds attack</Link> demonstrated how AI and automation could be used to create sophisticated, targeted attacks that remained undetected for months.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Mobile Device Vulnerabilities
          </h3>

          <p>
            Mobile devices are particularly susceptible to zero-click attacks due to:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Always-Connected Nature:</strong> Constant exposure to network-based attacks
            </li>
            <li>
              <strong>Complex Software Stacks:</strong> Multiple layers of software with potential vulnerabilities
            </li>
            <li>
              <strong>Rich Communication Features:</strong> Messaging, email, and media processing capabilities
            </li>
            <li>
              <strong>Resource Constraints:</strong> Limited computational resources for security monitoring - Learn about <Link to="/blog/vpn-security-guide" className={styles.link}>mobile security considerations</Link>
            </li>
          </ul>

          {/* YouTube Video Embed - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="mt-4">
              <h4 className="font-bold mb-2 text-red-600 dark:text-red-400">
                Zero-Click Attack Prevention Techniques
              </h4>
              <div className="aspect-w-16 aspect-h-9 w-full max-w-full overflow-hidden rounded-lg">
                <iframe
                className="w-full h-full max-w-full max-h-full"
                src="https://www.youtube.com/embed/[PREVENTION_TECHNIQUES_VIDEO_ID]"
                title="Zero-Click Attack Prevention Techniques"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
                </iframe>
              </div>
            </div>
          </div>
          */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Defense Strategies Against Zero-Click Attacks
          </h2>

          <p>
            Defending against zero-click attacks requires a multi-layered approach that goes beyond traditional security measures. The integration of artificial intelligence and machine learning into security systems, as discussed in our <Link to="/blog/ai-in-cybersecurity" className={styles.link}>AI in cybersecurity blog</Link>, promises to enhance detection and prevention capabilities for zero-click attacks.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Technical Mitigation Approaches
          </h3>

          <p>
            Organizations should implement:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Regular Patching:</strong> Keep all systems and applications updated to address known vulnerabilities
            </li>
            <li>
              <strong>Network Segmentation:</strong> Isolate critical systems to limit attack spread
            </li>
            <li>
              <strong>Behavioral Analysis:</strong> Monitor for unusual patterns that might indicate compromise
            </li>
            <li>
              <strong>Endpoint Detection and Response (EDR):</strong> Deploy advanced monitoring tools - Learn about <Link to="/toolkit" className={styles.link}>endpoint security tools</Link> that can help detect these attacks
            </li>
          </ul>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Mobile Security Best Practices
          </h3>

          <p>
            For mobile devices, implement:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Secure Communication Channels:</strong> Use encrypted messaging applications
            </li>
            <li>
              <strong>App Store Security:</strong> Only install applications from trusted sources
            </li>
            <li>
              <strong>Regular Updates:</strong> Keep operating systems and applications current
            </li>
            <li>
              <strong>Network Security:</strong> Avoid unsecured Wi-Fi networks - Learn about <Link to="/blog/vpn-safety-and-security" className={styles.link}>VPN safety practices</Link> for mobile devices
            </li>
          </ul>

          {/* Affiliate Product Recommendation - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="mt-4">
              <h4 className="font-bold mb-2 text-red-600 dark:text-red-400">
                Mobile Security Demo
              </h4>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/[MOBILE_SECURITY_DEMO_ID]"
                title="Mobile Security Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
              </iframe>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Real-time Threat Detection</h4>
                <p className="text-sm">Advanced mobile threat protection</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Zero-Click Protection</h4>
                <p className="text-sm">Defense against silent exploits</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Behavioral Analysis</h4>
                <p className="text-sm">Anomaly detection and response</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Privacy Protection</h4>
                <p className="text-sm">Comprehensive privacy controls</p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-lg hover:from-red-700 hover:to-orange-700 transition-all"
              >
                Get Mobile Security Solution
              </a>
            </div>
          </div>
          */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            The Role of AI in Zero-Click Attack Defense
          </h2>

          <p>
            Artificial intelligence is playing an increasingly important role in both attacking and defending against zero-click vulnerabilities. AI systems can analyze vast amounts of data to identify potential zero-click attack patterns before they can cause damage.
          </p>

          <p>
            Key AI applications in zero-click defense include:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Anomaly Detection:</strong> Identifying unusual communication patterns that might indicate an attack
            </li>
            <li>
              <strong>Threat Intelligence:</strong> Correlating global threat data to identify emerging zero-click exploits
            </li>
            <li>
              <strong>Automated Response:</strong> Implementing immediate countermeasures when threats are detected
            </li>
            <li>
              <strong>Predictive Analysis:</strong> Forecasting potential vulnerability exploitation - Learn about <Link to="/blog/cybersecurity-best-practices" className={styles.link}>AI in cybersecurity best practices</Link>
            </li>
          </ul>

          <p className="mt-4">
            The integration of AI into security systems represents a critical evolution in the ongoing arms race between attackers and defenders. As described in our <Link to="/blog/evolution-of-cybersecurity" className={styles.link}>evolution of cybersecurity blog</Link>, AI-powered defense systems are becoming essential for protecting against sophisticated threats like zero-click attacks.
          </p>

          {/* Final Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-pink-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Ready to Defend Against Zero-Click Attacks?</h3>
              <p className="mb-4">Get expert guidance on protecting your organization from sophisticated zero-click threats</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/toolkit"
                  className="px-6 py-3 bg-white text-purple-600 font-bold rounded-lg hover:bg-purple-50 transition-colors"
                >
                  Security Toolkit →
                </Link>
                <Link
                  to="/decades"
                  className="px-6 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-purple-600 transition-colors"
                >
                  Historical Events →
                </Link>
              </div>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Future of Zero-Click Security
          </h2>

          <p>
            As we look toward the future, zero-click attacks are likely to become more sophisticated and widespread. The integration of AI and machine learning will continue to drive both attack and defense capabilities forward. Organizations must prepare for an evolving threat landscape where zero-click attacks become increasingly common and sophisticated.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Emerging Trends
          </h3>

          <p>
            Key trends in zero-click attack evolution include:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>IoT Vulnerabilities:</strong> Increasing attack surface with connected devices
            </li>
            <li>
              <strong>5G Exploitation:</strong> New attack vectors in high-speed networks
            </li>
            <li>
              <strong>AI-Enhanced Targeting:</strong> More precise and effective attacks
            </li>
            <li>
              <strong>Supply Chain Integration:</strong> Zero-click attacks through software updates - Learn about <Link to="/blog/ransomware-as-a-service" className={styles.link}>supply chain security considerations</Link>
            </li>
          </ul>

          <p className="mt-4">
            The rise of quantum computing and new attack vectors creates fresh challenges, but lessons learned from historical incidents like the <Link to="/event/cyber-attacks-on-Estonia-2007" className={styles.link}>Estonia cyber attacks</Link> continue to inform modern defense strategies.
          </p>

          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg mt-8">
            <h3 className="font-bold text-lg text-blue-800 dark:text-blue-200 mb-2">
              Key Takeaway
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              Zero-click attacks represent one of the most sophisticated and dangerous cybersecurity threats in 2026, requiring no user interaction to compromise devices. These attacks exploit fundamental vulnerabilities in communication protocols and system components, making them particularly challenging to defend against. Success in defending against zero-click attacks requires a comprehensive approach that combines regular patching, advanced monitoring, behavioral analysis, and AI-powered threat detection. Understanding zero-click attack mechanisms and implementing appropriate defenses is essential for maintaining security in an increasingly connected world. Learn more about zero-click attacks by exploring our <Link to="/decades" className={styles.link}>complete history of cyber attacks</Link> and <Link to="/toolkit" className={styles.link}>recommended security tools</Link>.
            </p>
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-4">
            <Link
              to="/blog"
              className={styles.link}
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/decades"
              className={styles.link}
            >
              Explore Cyber Security History →
            </Link>
            <Link
              to="/toolkit"
              className={styles.link}
            >
              Security Tools →
            </Link>
            <Link
              to="/blog/common-cyber-attacks"
              className={styles.link}
            >
              Common Cyber Attacks →
            </Link>
            <Link
              to="/blog/ai-driven-cyber-attacks"
              className={styles.link}
            >
              AI-Driven Attacks →
            </Link>
            <Link
              to="/blog/cyber-attacks-2025"
              className={styles.link}
            >
              2025 Cyber Attacks →
            </Link>
            <Link
              to="/blog/what-is-cyber-security"
              className={styles.link}
            >
              Cybersecurity Basics →
            </Link>
            <Link
              to="/blog/ransomware-as-a-service"
              className={styles.link}
            >
              Ransomware-as-a-Service →
            </Link>
            <Link
              to="/blog/zero-trust-architecture"
              className={styles.link}
            >
              Zero Trust Architecture →
            </Link>
            <Link
              to="/blog/vpn-security-guide"
              className={styles.link}
            >
              VPN Security Guide →
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3">
              Related Cyber Attacks:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Link to="/event/pegasus-spyware" className={styles.link}>
                Pegasus Spyware Attack
              </Link>
              <Link to="/event/stuxnet" className={styles.link}>
                Stuxnet Attack (2010)
              </Link>
              <Link to="/event/sony-pictures-hack-2014" className={styles.link}>
                Sony Pictures Hack (2014)
              </Link>
              <Link to="/event/solarwinds-supply-chain-attack" className={styles.link}>
                SolarWinds Attack (2020)
              </Link>
              <Link to="/event/colonial-pipeline-ransomware" className={styles.link}>
                Colonial Pipeline Attack (2021)
              </Link>
              <Link to="/event/equifax-data-breach-2017" className={styles.link}>
                Equifax Data Breach (2017)
              </Link>
              <Link to="/event/wannacry" className={styles.link}>
                WannaCry Ransomware (2017)
              </Link>
              <Link to="/event/notpetya" className={styles.link}>
                NotPetya Attack (2017)
              </Link>
              <Link to="/event/log4j-log4shell-2021" className={styles.link}>
                Log4j Vulnerability (2021)
              </Link>
              <Link to="/event/cyber-attacks-on-Estonia-2007" className={styles.link}>
                Estonia Cyber Attacks (2007)
              </Link>
              <Link to="/event/morris-worm-1988" className={styles.link}>
                Morris Worm (1988)
              </Link>
              <Link to="/event/iloveyou" className={styles.link}>
                ILOVEYOU Virus (2000)
              </Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default ZeroClickAttacks;